import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { CompetitorCards, ArchetypeCards, ChannelBars, AnglePillars, Roadmap, EconomicsTable, PositioningBlock } from './ResearchBlocks.jsx';
import '../../styles/research-print.css';

// Chromeless, print-first render of a single research report. Fetches the SAME
// authenticated, user-scoped record the on-screen report uses (so it is auth-
// gated and cannot leak another user's data — the worker 404s a foreign id),
// lays it out as a Dathent-branded A4 document (cover → content → sources →
// back cover, ported from research-14-pdf-report), then calls window.print().
// CPU-light: the browser does the PDF, no server-side headless render.

function unwrap(res) { return res?.data ?? res; }

const DOM_COLORS = ['#7A5BFF', '#0E6E6B', '#C9A24B', '#1F4E79', '#B8860B', '#4285F4', '#0E7C66', '#9D4EDD', '#D1495B', '#2A9D8F'];
function domColor(domain) {
  let hash = 0;
  const s = String(domain || '');
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return DOM_COLORS[hash % DOM_COLORS.length];
}
function fmtDate(value, lang) {
  if (!value) return '';
  try { return new Date(value).toLocaleDateString(lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (_) { return String(value).slice(0, 10); }
}
function fmtNum(n) {
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) < 10 ? 1 : 0 });
}

// Build id → {number, domain, letter, color} so inline [^src_xxx] markers render
// as clean numbered citation chips (matching the on-screen report).
function buildSourceIndex(sources) {
  const byId = new Map();
  (sources || []).forEach((s, i) => {
    byId.set(s.id, { number: i + 1, domain: s.domain || '', letter: (s.domain || '?')[0].toUpperCase(), color: domColor(s.domain) });
  });
  return byId;
}

// Inline tokens: **bold** | [^src_id] | (assumption: …) → React nodes.
function renderInline(text, sIndex, keyPrefix) {
  const out = [];
  const pattern = /(\*\*[^*]+\*\*)|(\[\^src_[a-z0-9]+\])|(\(assumption:[^)]*\))/g;
  let last = 0; let m; let i = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      out.push(<strong key={`${keyPrefix}-b${i}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('[^')) {
      const src = sIndex.get(tok.slice(2, -1));
      if (src) {
        out.push(
          <span key={`${keyPrefix}-c${i}`} className="rp-cite">
            <i style={{ background: src.color }}>{src.letter}</i>{src.number}
          </span>
        );
      }
    } else {
      out.push(<span key={`${keyPrefix}-a${i}`} className="rp-assume">≈ {tok.slice('(assumption:'.length, -1).trim()}</span>);
    }
    last = m.index + tok.length; i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Table → chart data (mirrors on-screen ResearchTab): a single number → lo===hi,
// a range ("2.13–2.40") → both bounds (drawn as a band, never a fabricated point).
function parseCell(s) {
  const str = String(s ?? '');
  const m = str.match(/(-?\d[\d.,]*)\s*[-–—]\s*(-?\d[\d.,]*)/);
  if (m) {
    const lo = parseFloat(m[1].replace(/,/g, '')); const hi = parseFloat(m[2].replace(/,/g, ''));
    if (Number.isFinite(lo) && Number.isFinite(hi)) return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
  }
  const v = parseFloat(str.replace(/,/g, '').replace(/[^0-9.\-]/g, ' ').trim());
  return Number.isFinite(v) ? { lo: v, hi: v } : null;
}
function tableData(rows) {
  if (!Array.isArray(rows) || rows.length < 3) return null;
  const [head, ...data] = rows;
  const points = []; let range = false;
  for (const r of data) {
    if (!Array.isArray(r) || r.length !== 2) return null;
    const c = parseCell(r[1]);
    if (!c) return null;
    if (c.lo !== c.hi) range = true;
    points.push({ label: String(r[0] ?? '').trim(), lo: c.lo, hi: c.hi, value: (c.lo + c.hi) / 2 });
  }
  if (points.length < 2) return null;
  // Only chart a TIME SERIES (every label carries a year) — categorical tables
  // stay plain, so a non-trend table is never drawn as a misleading line.
  if (!points.every((p) => /\b(?:19|20)\d{2}\b/.test(p.label))) return null;
  return { range, points, caption: String(head?.[1] ?? '').trim() };
}
function sameValues(points, skip) {
  if (!Array.isArray(points) || !Array.isArray(skip) || points.length !== skip.length) return false;
  return points.every((p, i) => String(p.label) === String(skip[i].label) && Number(p.value) === Number(skip[i].value));
}
function seriesStat(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const c = cagrPct(points.map((p) => ({ label: p.label, value: p.value })));
  if (c != null) return `CAGR ${c.toFixed(1)}%`;
  const v0 = Number(points[0].value); const v1 = Number(points[points.length - 1].value);
  if (!(v0 > 0) || !Number.isFinite(v1)) return null;
  const d = Math.round(((v1 - v0) / v0) * 100);
  return `${d >= 0 ? '+' : ''}${d}%`;
}
// TAM/SAM/SOM "Market Segmentation" table → tier descriptor (mirrors ResearchTab).
function segmentationData(rows) {
  if (!Array.isArray(rows) || rows.length < 3) return null;
  const [head, ...data] = rows;
  const tiers = [];
  for (const r of data) {
    if (!Array.isArray(r) || r.length < 2) return null;
    const seg = String(r[0] ?? '').trim();
    const m = /\b(tam|sam|som)\b/i.exec(seg);
    if (!m) return null; // every row must be a TAM/SAM/SOM tier
    const v = parseFloat(String(r[1] ?? '').replace(/,/g, '').replace(/[^0-9.\-]/g, ' ').trim());
    tiers.push({
      key: m[1].toUpperCase(),
      label: seg.replace(/\b(tam|sam|som)\b/i, '').replace(/^[\s([]+|[\s)\]]+$/g, '').trim(),
      value: Number.isFinite(v) ? v : null,
      note: r[2] != null ? String(r[2]).trim() : '',
    });
  }
  if (tiers.length < 2 || !tiers.some((o) => o.value != null)) return null;
  const unit = String(head?.[1] ?? '').replace(/value/i, '').replace(/[()]/g, '').trim();
  return { tiers, unit };
}
function PrintSegmentCards({ tiers, unit, color, sIndex }) {
  return (
    <div className="rp-seg">
      {tiers.map((r, i) => {
        const prev = i > 0 ? tiers[i - 1].value : null;
        // Share of the previous tier; keep a decimal for sub-1% so a true 0.26%
        // never collapses to a misleading "0%".
        const ratio = (prev && prev > 0 && r.value != null) ? (r.value / prev) * 100 : null;
        const pct = ratio == null ? null : (ratio >= 1 ? Math.round(ratio).toString() : ratio.toFixed(ratio >= 0.1 ? 1 : 2));
        const arrow = ratio == null ? '' : (ratio > 100 ? '↑' : '↓'); // TAM≥SAM≥SOM normally; ↑ only if source data inverts
        return (
          <div key={i} className="rp-seg-card" style={{ borderTopColor: color }}>
            <div className="rp-seg-top">
              <span className="rp-seg-k">{r.key}</span>
              {pct != null && <span className="rp-seg-pct">{arrow} {pct}%</span>}
            </div>
            <div className="rp-seg-v">{r.value != null ? fmtNum(r.value) : '—'}{r.value != null && unit ? <span className="rp-seg-u"> {unit}</span> : null}</div>
            {r.label && <div className="rp-seg-lbl">{r.label}</div>}
            {r.note && <div className="rp-seg-note">{renderInline(r.note, sIndex, `seg-${i}`)}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Static (no-JS) per-table chart for print: a caption (value-column name) + a
// headline stat, then a growth line (plain numbers) or a lo→hi band (ranges).
// Wrapped in .rp-chart so it never splits a page; no markers (no distortion).
function PrintTableChart({ data, color }) {
  const W = 540; const H = 92; const pad = 4; const top = 8; const bot = 18;
  const pts = data.points;
  const allv = pts.flatMap((p) => [p.lo, p.hi]);
  const min = Math.min(...allv); const max = Math.max(...allv); const span = max - min || 1;
  const x = (i) => pad + (pts.length === 1 ? (W - pad * 2) / 2 : (i / (pts.length - 1)) * (W - pad * 2));
  const y = (v) => top + (1 - (v - min) / span) * (H - top - bot);
  const stat = seriesStat(pts);
  const grid = [0, 0.5, 1].map((g) => top + g * (H - top - bot));
  const lp = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
  let body;
  if (data.range) {
    // A central-estimate trend line over a faint lo→hi band + whisker ticks —
    // reads like a normal growth chart with a range shadow (mirrors on-screen).
    const tp = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.hi).toFixed(1)}`);
    const bp = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.lo).toFixed(1)}`).reverse();
    body = <>
      <path d={`M ${tp.join(' L ')} L ${bp.join(' L ')} Z`} fill={color} opacity="0.13" />
      {pts.map((p, i) => (p.lo !== p.hi ? <line key={i} x1={x(i)} y1={y(p.hi)} x2={x(i)} y2={y(p.lo)} stroke={color} strokeWidth="1.6" opacity="0.45" /> : null))}
      <path d={`M ${lp.join(' L ')}`} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </>;
  } else {
    body = <><path d={`M ${x(0).toFixed(1)},${H - bot} L ${lp.join(' L ')} L ${x(pts.length - 1).toFixed(1)},${H - bot} Z`} fill={color} opacity="0.12" /><path d={`M ${lp.join(' L ')}`} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></>;
  }
  return (
    <div className="rp-chart rp-tblchart">
      {(data.caption || stat) && <div className="rp-tbc-h"><span className="rp-tbc-cap">{data.caption}</span>{stat ? <span className="rp-tbc-stat">{stat}</span> : null}</div>}
      <svg className="rp-tblsvg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <g className="rp-gridln">{grid.map((gy, i) => <line key={i} x1={pad} y1={gy} x2={W - pad} y2={gy} />)}</g>
        {body}
        <g className="rp-tbl-axis" textAnchor="middle">{pts.map((p, i) => <text key={i} x={x(i)} y={H - 5}>{p.label}</text>)}</g>
      </svg>
    </div>
  );
}

// Markdown → React for the report bodies. Strips the embedded "Sources:" footnote
// blocks the analyst sections append (bare/defined [^src_xxx] lines + the divider),
// which otherwise leak as raw text in the PDF. Inline markers become citation chips.
function PrintMarkdown({ body, sIndex, chartTables = false, skipSeries = null, palette }) {
  if (!body) return null;
  const pal = (Array.isArray(palette) && palette.length) ? palette : ['#7A5BFF'];
  let chartIdx = 0; // each table chart gets a distinct (non-hero) palette colour
  const lines = String(body).replace(/\r/g, '').split('\n');
  const out = [];
  let list = null; let table = null; let key = 0;
  const flushList = () => { if (list) { out.push(<ul key={key++} className="rp-ul">{list}</ul>); list = null; } };
  const flushTable = () => {
    if (table && table.length) {
      // TAM/SAM/SOM segmentation table → tier cards (replaces the raw table).
      const seg = chartTables ? segmentationData(table) : null;
      if (seg) {
        out.push(
          <div key={key++} className="rp-tblwrap">
            <PrintSegmentCards tiers={seg.tiers} unit={seg.unit} color={pal[0]} sIndex={sIndex} />
          </div>
        );
        table = null; return;
      }
      const [head, ...rows] = table;
      const data = chartTables ? tableData(table) : null;
      const showChart = data && !(skipSeries && sameValues(data.points, skipSeries));
      const c = showChart ? pal[(chartIdx++ + 1) % pal.length] : null;
      const tbl = (
        <table className="rp-tbl">
          <thead><tr>{head.map((cc, ci) => <th key={ci}>{renderInline(cc, sIndex, `th${key}-${ci}`)}</th>)}</tr></thead>
          <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((cc, ci) => <td key={ci} className={/^[$€¥£]?[\d.,%–-]+$/.test(cc.trim()) ? 'num' : ''}>{renderInline(cc, sIndex, `td${key}-${ri}-${ci}`)}</td>)}</tr>)}</tbody>
        </table>
      );
      out.push(
        <div key={key++} className="rp-tblwrap">
          {showChart && <PrintTableChart data={data} color={c} />}
          {tbl}
        </div>
      );
    }
    table = null;
  };
  for (const raw of lines) {
    const tt = raw.trim();
    // strip leaked footnote defs / bare markers / "Sources:" labels / hr dividers
    if (/^\[\^src_[a-z0-9]+\]/.test(tt)) continue;
    if (/^sources:?\s*$/i.test(tt)) continue;
    if (tt === '---' || tt === '***') continue;
    if (!tt) { flushList(); flushTable(); continue; }
    if (/^\|.*\|$/.test(tt)) {
      if (/^\|[\s:|-]+\|$/.test(tt)) continue; // separator row
      flushList();
      const cells = tt.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      table = table || []; table.push(cells); continue;
    }
    flushTable();
    const h = tt.match(/^(#{1,4})\s+(.*)$/);
    if (h) { flushList(); out.push(<h4 key={key++} className="rp-h">{renderInline(h[2], sIndex, `h${key}`)}</h4>); continue; }
    if (/^[-*]\s+/.test(tt)) { (list = list || []).push(<li key={key++}>{renderInline(tt.replace(/^[-*]\s+/, ''), sIndex, `li${key}`)}</li>); continue; }
    flushList();
    out.push(<p key={key++}>{renderInline(tt, sIndex, `p${key}`)}</p>);
  }
  flushList(); flushTable();
  return <>{out}</>;
}

// ---------- print-native charts (light, pack-styled) ----------

function cagrPct(series) {
  if (!series || series.length < 2) return null;
  const first = series[0]; const lastP = series[series.length - 1];
  const y0 = parseFloat(String(first.label).replace(/[^\d.]/g, ''));
  const y1 = parseFloat(String(lastP.label).replace(/[^\d.]/g, ''));
  const years = (Number.isFinite(y0) && Number.isFinite(y1) && y1 > y0) ? (y1 - y0) : (series.length - 1);
  if (!first.value || years <= 0) return null;
  const r = (Math.pow(lastP.value / first.value, 1 / years) - 1) * 100;
  return Number.isFinite(r) ? r : null;
}

function Kpis({ market, t }) {
  const cards = [];
  if (market.tam != null) cards.push({ l: t('research.metric_tam'), v: fmtNum(market.tam), u: market.unit, s: t('research.chartMarketTitle') });
  const c = cagrPct(market.series);
  if (c != null) cards.push({ l: t('research.metric_cagr'), v: c.toFixed(1), u: '%', s: t('research.chartGrowthCap') });
  if (market.sam != null) cards.push({ l: t('research.metric_sam'), v: fmtNum(market.sam), u: market.unit, s: '' });
  if (market.som != null) cards.push({ l: t('research.metric_som'), v: fmtNum(market.som), u: market.unit, s: '' });
  if (!cards.length) return null;
  return (
    <div className="rp-kpis" style={{ gridTemplateColumns: `repeat(${cards.length}, 1fr)` }}>
      {cards.map((k, i) => (
        <div key={i} className="rp-kpi">
          <div className="l">{k.l}</div>
          <div className="v">{k.v}{k.u ? <span className="u"> {k.u}</span> : null}</div>
          {k.s ? <div className="s">{k.s}</div> : null}
        </div>
      ))}
    </div>
  );
}

function GrowthChart({ series, unit, color, t }) {
  const W = 560; const H = 188; const padL = 6; const padR = 6; const top = 16; const bot = 30;
  const vals = series.map((p) => p.value);
  const min = Math.min(...vals, 0); const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => padL + (series.length === 1 ? 0 : (i / (series.length - 1)) * (W - padL - padR));
  const y = (v) => top + (1 - (v - min) / span) * (H - top - bot);
  const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `M ${x(0).toFixed(1)},${H - bot} L ${pts.join(' L ')} L ${x(series.length - 1).toFixed(1)},${H - bot} Z`;
  const grid = [0, 0.5, 1].map((g) => top + g * (H - top - bot));
  return (
    <div className="rp-chart">
      <div className="rp-chart-h"><span className="rp-chart-t">{t('research.chartGrowthCap')}{unit ? ` · ${unit}` : ''}</span></div>
      <svg className="rp-chartsvg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs><linearGradient id="rpArea" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity="0.20" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
        <g className="rp-gridln">{grid.map((gy, i) => <line key={i} x1={padL} y1={gy} x2={W - padR} y2={gy} />)}</g>
        <path d={area} fill="url(#rpArea)" />
        <path d={line} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        {series.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.value)} r="3" fill="#fff" stroke={color} strokeWidth="2" />)}
        {/* value labels above each point (the years alone don't say how big the value is) */}
        <g className="rp-cval">{series.map((p, i) => {
          const anchor = i === 0 ? 'start' : (i === series.length - 1 ? 'end' : 'middle');
          return <text key={i} x={x(i)} y={Math.max(9, y(p.value) - 7)} textAnchor={anchor}>{fmtNum(p.value)}</text>;
        })}</g>
        <g className="rp-axis" textAnchor="middle">{series.map((p, i) => <text key={i} x={x(i)} y={H - 8}>{p.label}</text>)}</g>
      </svg>
    </div>
  );
}

function Funnel({ market, t, color }) {
  const rows = [['metric_tam', market.tam], ['metric_sam', market.sam], ['metric_som', market.som]].filter(([, v]) => v != null);
  if (rows.length < 2) return null;
  // Fixed/illustrative widths (TAM..SOM ratios are extreme — a to-scale bar would
  // make SAM/SOM vanish); the real values + the conversion % carry the truth.
  const widths = [100, 62, 34];
  const fades = [1, 0.74, 0.5];
  const c = color || '#7A5BFF';
  return (
    <div className="rp-chart">
      <div className="rp-chart-h"><span className="rp-chart-t">TAM · SAM · SOM</span></div>
      <div className="rp-funnel">
        {rows.map(([k, v], i) => {
          const prevV = i > 0 ? rows[i - 1][1] : null;
          const ratio = (prevV && prevV > 0) ? (v / prevV) * 100 : null;
          // Decimal for sub-1% so an honest 0.26% never reads as "0%".
          const conv = ratio == null ? null : (ratio >= 1 ? Math.round(ratio).toString() : ratio.toFixed(ratio >= 0.1 ? 1 : 2));
          return (
            <div key={k} className="rp-funnel-row">
              <span className="rp-fn-k">{t(`research.${k}`)}{conv != null ? <em> ↓ {conv}%</em> : null}</span>
              <div className="rp-fn-track">
                <span className="rp-fn-barwrap"><span className="rp-fn-bar" style={{ width: `${widths[i]}%`, background: c, opacity: fades[i] }} /></span>
                <span className="rp-fn-v">{fmtNum(v)}{market.unit ? ` ${market.unit}` : ''}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Donut({ segments, colors, t }) {
  const total = segments.reduce((s, x) => s + x.share, 0) || 1;
  const R = 34; const C = 2 * Math.PI * R; let acc = 0;
  return (
    <div className="rp-chart">
      <div className="rp-chart-h"><span className="rp-chart-t">{t('research.chartAudienceTitle')}</span></div>
      <div className="rp-donut">
        <svg viewBox="0 0 90 90" className="rp-donutsvg">
          <g transform="translate(45,45)">
            <circle r={R} fill="none" stroke="var(--line)" strokeWidth="14" />
            <g transform="rotate(-90)">
              {segments.map((seg, i) => {
                const dash = (seg.share / total) * C;
                const node = <circle key={i} r={R} fill="none" stroke={colors[i % colors.length]} strokeWidth="14" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc} />;
                acc += dash; return node;
              })}
            </g>
          </g>
        </svg>
        <ul className="rp-donut-leg">
          {segments.map((seg, i) => (
            <li key={i}><i style={{ background: colors[i % colors.length] }} /><span>{seg.label}</span><b>{Math.round((seg.share / total) * 100)}%</b></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Pull {tam,sam,som,unit} from the first TAM/SAM/SOM segmentation table in a
// body's markdown (mirrors ResearchTab.segFromBody) — the structured extractor
// often leaves sam/som null even when the body states them.
function segFromBody(bodyMd) {
  if (!bodyMd || typeof bodyMd !== 'string') return null;
  let table = null;
  const resolve = () => {
    if (!table) return null;
    const seg = segmentationData(table);
    table = null;
    if (!seg) return null;
    const o = { unit: seg.unit || '' };
    for (const tr of seg.tiers) {
      const k = String(tr.key).toLowerCase();
      if (tr.value != null && (k === 'tam' || k === 'sam' || k === 'som')) o[k] = tr.value;
    }
    return (o.tam != null || o.sam != null || o.som != null) ? o : null;
  };
  for (const raw of bodyMd.split('\n')) {
    const tt = raw.trim();
    if (/^\|.*\|$/.test(tt)) {
      if (/^\|[\s:|-]+\|$/.test(tt)) continue;
      const cells = tt.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      (table = table || []).push(cells);
    } else if (table) {
      const r = resolve();
      if (r) return r;
    }
  }
  return resolve();
}
// Backfill market.sam/som (and tam) from the body when the structured extractor
// left them null (mirrors ResearchTab.enrichMarket). Never overwrites a present
// value; unit-guarded by a TAM-scale match so units never mix.
function enrichMarket(market, sections) {
  if (!market) return market;
  if (market.sam != null && market.som != null) return market;
  let seg = null;
  for (const s of (sections || [])) {
    if (!s || !s.bodyMd) continue;
    const found = segFromBody(s.bodyMd);
    if (found) { seg = found; break; }
  }
  if (!seg) return market;
  const out = { ...market };
  if (market.tam != null) {
    if (seg.tam == null) return market;
    const ratio = seg.tam / market.tam;
    if (!(ratio > 0.8 && ratio < 1.25)) return market;
    if (out.sam == null && seg.sam != null) out.sam = seg.sam;
    if (out.som == null && seg.som != null) out.som = seg.som;
    if (!out.unit && seg.unit) out.unit = seg.unit; // TAM-match confirmed same unit
  } else {
    if (seg.tam != null) out.tam = seg.tam;
    if (out.sam == null && seg.sam != null) out.sam = seg.sam;
    if (out.som == null && seg.som != null) out.som = seg.som;
    if (!out.unit && seg.unit) out.unit = seg.unit;
  }
  return out;
}

const DLOGO = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M12 2 14 9l7 .5-5.5 4.4L17.5 21 12 17.1 6.5 21l1.8-7.1L3 9.5 10 9z" opacity=".95" /></svg>
);

export default function ResearchPrint() {
  const { reportId } = useParams();
  const { t, i18n } = useTranslation();
  const [report, setReport] = useState(null);
  const [brand, setBrand] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rep = unwrap(await api.research.get(reportId))?.report;
        if (cancelled) return;
        if (!rep || rep.status !== 'ready') { setState('error'); return; }
        setReport(rep);
        try { const bp = unwrap(await api.profile.getBrandProfile()); if (!cancelled) setBrand(bp?.brandProfile || bp?.data || bp); } catch (_) { /* no brand */ }
        try {
          const al = unwrap(await api.brandAssets.list());
          const assets = Array.isArray(al?.assets) ? al.assets : [];
          const logo = assets.find((a) => a.id === al?.primaryLogoId) || assets.find((a) => a.isPrimaryLogo);
          if (!cancelled && logo?.url) setLogoUrl(logo.url);
        } catch (_) { /* no logo */ }
        if (!cancelled) setState('ready');
      } catch (_) {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  // Auto-open the print dialog once content + fonts have settled.
  useEffect(() => {
    if (state !== 'ready') return undefined;
    const id = setTimeout(() => { try { window.print(); } catch (_) { /* user can use the button */ } }, 700);
    return () => clearTimeout(id);
  }, [state]);

  // The browser uses document.title as the default "Save as PDF" filename — set
  // it to the actual research subject + brand (kept under "Dathent") instead of
  // the app's generic title.
  useEffect(() => {
    if (state !== 'ready') return undefined;
    const bn = (brand?.businessName || '').trim();
    const prev = document.title;
    document.title = bn ? `Dathent — ${t('research.reportTitle')} — ${bn}` : `Dathent — ${t('research.reportTitle')}`;
    return () => { document.title = prev; };
  }, [state, brand, t]);

  if (state === 'loading') return <div className="rp-msg">{t('research.loading')}</div>;
  if (state === 'error' || !report) return <div className="rp-msg">{t('research.printNotAvailable')}</div>;

  const brandName = (brand?.businessName || '').trim();
  const brandColors = Array.isArray(brand?.brandColors) ? brand.brandColors.filter(Boolean).slice(0, 5) : [];
  const palette = brandColors.length ? [...brandColors, '#7A5BFF', '#0E6E6B', '#C9A24B'] : ['#7A5BFF', '#0E6E6B', '#C9A24B', '#4FC3FF', '#E455CF'];
  const sections = (report.sections || []).filter((s) => !s.locked && s.bodyMd);
  const sources = report.sources || [];
  const sIndex = buildSourceIndex(sources);
  const dateStr = fmtDate(report.completedAt || report.createdAt, i18n.language);
  const metrics = report.metrics || null;
  const blocks = report.blocks || null;
  // Backfill sam/som from the body's TAM/SAM/SOM table so the PDF exec KPIs +
  // funnel match what the report actually contains (same as the on-screen view).
  const market = enrichMarket(metrics?.market, sections);
  const hasMetrics = !!(metrics && (metrics.market || metrics.audience));
  const domains = [...new Set(sources.map((s) => s.domain).filter(Boolean))];
  const runHead = (
    <div className="rp-rh"><span className="b"><span className="rp-rh-mark" />Dathent Research</span><span>{brandName ? `${brandName} · ` : ''}v{report.version}</span></div>
  );

  return (
    <div className="rp">
      <div className="rp-toolbar no-print">
        <button type="button" onClick={() => window.print()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" /></svg>
          {t('research.savePdf')}
        </button>
        <span className="rp-toolbar-hint">{t('research.printHint')}</span>
      </div>

      {/* ===== COVER ===== */}
      <section className="rp-page rp-cover">
        <div className="rp-cover-top">
          <span className="rp-logo">{DLOGO}</span>
          <div><div className="rp-logo-name">Dathent</div><div className="rp-logo-sub">{t('research.printEyebrow')}</div></div>
        </div>
        <div className="rp-cover-mid">
          <div className="rp-cover-eyebrow">{t('research.activityLabel')}</div>
          <h1>{t('research.reportTitle')}{brandName ? <> <span className="it">{brandName}</span></> : null}</h1>
          <div className="rp-cover-facts">
            <div><div className="rp-cf-l">{t('research.printDate')}</div><div className="rp-cf-v">{dateStr}</div></div>
            <div><div className="rp-cf-l">{t('research.printVersion')}</div><div className="rp-cf-v">v{report.version}</div></div>
            <div><div className="rp-cf-l">{t('research.sourcesTitle')}</div><div className="rp-cf-v">{sources.length}{domains.length ? ` · ${domains.length}` : ''}</div></div>
            {brandColors.length > 0 && (
              <div><div className="rp-cf-l">Brand</div><div className="rp-cf-v"><span className="rp-sw">{brandColors.map((c, i) => <i key={i} style={{ background: c }} />)}</span></div></div>
            )}
          </div>
        </div>
        <div className="rp-cover-bottom">
          {logoUrl
            ? <span className="rp-client-logo"><img src={logoUrl} alt="" /></span>
            : <span className="rp-client-logo rp-client-mono"><b>{(brandName[0] || 'D').toUpperCase()}</b></span>}
          <div className="rp-conf"><b>{t('research.printConfidential')}</b><br />{t('research.printPreparedFor')}<br />{t('research.printFooter')}</div>
        </div>
      </section>

      {/* ===== EXEC + KEY METRICS ===== */}
      {(report.summary || hasMetrics) && (
        <section className="rp-page rp-body">
          {runHead}
          <div className="rp-pad">
            <div className="rp-sec-no">01</div>
            <h2 className="rp-sec-h">{t('research.execTitle')}</h2>
            <div className="rp-divider" />
            {report.summary && (
              <div className="rp-exec"><div className="rp-exec-h">{t('research.execEyebrow')}</div><p>{report.summary}</p></div>
            )}
            {market && <Kpis market={market} t={t} />}
            {market?.series && <GrowthChart series={market.series} unit={market.unit} color={palette[0]} t={t} />}
            {market && <Funnel market={market} t={t} color={palette[0]} />}
            {metrics?.audience?.segments && <Donut segments={metrics.audience.segments} colors={palette} t={t} />}
          </div>
        </section>
      )}

      {/* ===== CONTENT SECTIONS ===== */}
      {sections.map((s, i) => (
        <section key={s.id} className="rp-page rp-body">
          {runHead}
          <div className="rp-pad">
            <div className="rp-sec-no">{String(i + 2).padStart(2, '0')}</div>
            <h2 className="rp-sec-h">{s.title}</h2>
            <div className="rp-divider" />
            {s.kind === 'competitors' && blocks?.competitors && <CompetitorCards items={blocks.competitors} t={t} />}
            {s.kind === 'audience' && blocks?.archetypes && <ArchetypeCards items={blocks.archetypes} t={t} />}
            {s.kind === 'channels' && blocks?.channels && <ChannelBars items={blocks.channels} colors={palette} t={t} />}
            {s.kind === 'content_angles' && blocks?.angles && <AnglePillars items={blocks.angles} colors={palette} t={t} />}
            {s.kind === 'action_plan' && blocks?.roadmap && <Roadmap phases={blocks.roadmap.phases} kpis={blocks.roadmap.kpis} t={t} />}
            {s.kind === 'economics' && blocks?.economics && <EconomicsTable data={blocks.economics} t={t} />}
            {s.kind === 'positioning' && blocks?.positioning && <PositioningBlock data={blocks.positioning} t={t} />}
            <PrintMarkdown
              body={s.bodyMd}
              sIndex={sIndex}
              chartTables
              skipSeries={s.kind === 'market' ? metrics?.market?.series : null}
              palette={palette}
            />
          </div>
        </section>
      ))}

      {/* ===== SOURCES ===== */}
      {sources.length > 0 && (
        <section className="rp-page rp-body">
          {runHead}
          <div className="rp-pad">
            <div className="rp-sec-no">{t('research.verifiedReferences')}</div>
            <h2 className="rp-sec-h">{t('research.sourcesTitle')}</h2>
            <div className="rp-divider" />
            <div className="rp-srcgrid">
              {sources.map((s, i) => (
                <div key={s.id} className="rp-src">
                  <i style={{ background: domColor(s.domain) }}>{(s.domain || '?')[0].toUpperCase()}</i>
                  <div className="rp-src-main">
                    <span className="rp-src-n mono">{i + 1}</span>
                    {s.url ? <a href={s.url} className="rp-src-t">{s.title || s.domain}</a> : <span className="rp-src-t">{s.title || s.domain}</span>}
                    <span className="rp-src-d mono">{s.domain}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== BACK COVER ===== */}
      <section className="rp-page rp-back">
        <div className="rp-back-mark"><span className="rp-logo">{DLOGO}</span><div><div className="rp-logo-name">Dathent</div><div className="rp-logo-sub">{t('research.printEyebrow')}</div></div></div>
        <h2>{t('research.printBackTitle')}</h2>
        <p>{t('research.printBackBody')}</p>
        <div className="rp-back-foot"><span>dathent.ai</span><span>v{report.version} · {dateStr}</span><span>{t('research.printFooter')}</span></div>
      </section>
    </div>
  );
}
